import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, 
  Fuel, 
  Wrench, 
  Car,
  Truck,
  Shield, 
  ArrowRight, 
  ArrowDown,
  CheckCircle, 
  BarChart3,
  Users,
  Clock,
  DollarSign,
  Settings,
  Target,
  TrendingUp,
  Globe,
  Network,
  Layers,
  MapPin,
  Gauge,
  FileText,
  Headphones,
  Star,
  Building,

  Database,
  Smartphone,
  Cpu,
  Activity,
  Eye,
  Repeat,
  Filter,
  Coins,
  PieChart
} from 'lucide-react';
import { Container, Button, Card, SectionTitle } from '../components';
import { useInView } from '../hooks';

/**
 * 车后服务页面 - 完全重构版本
 * 基于精校后的内容，按照四个模块重新组织：概述、平台壁垒与差异化特点、两端问题解决方案、对您的收益
 */
const CarServicePage: React.FC = () => {
  // 视图检测钩子
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [overviewRef, overviewInView] = useInView({ threshold: 0.2 });
  const [advantagesRef, advantagesInView] = useInView({ threshold: 0.2 });
  const [solutionsRef, solutionsInView] = useInView({ threshold: 0.2 });
  const [benefitsRef, benefitsInView] = useInView({ threshold: 0.2 });

  // 平台壁垒与差异化特点（7个核心特点）
  const platformAdvantages = [
    {
      title: '双高频流量池',
      description: '聚合网约车、出租车、物流司机的高频补能与维保需求，日/隔日触点驱动强复购与低获客成本。',
      icon: <Users className="w-8 h-8" />
    },
    {
      title: '流量撮合',
      description: '基于LBS、时段、热力的多模态推荐（图文、语音播报、地图POI、围栏推送），提升到站转化与峰谷错峰效率。',
      icon: <MapPin className="w-8 h-8" />
    },
    {
      title: '经营中台与数据资产',
      description: '从站效/桩利用到流量渗透的全链路指标沉淀，反向优化供给结构与活动ROI。',
      icon: <BarChart3 className="w-8 h-8" />
    },
    {
      title: '标准化对接能力',
      description: '订单/核销/库存/价格/会员/发票/结算的标准API＋SDK，降低改造成本，适配连锁与单店。',
      icon: <Settings className="w-8 h-8" />
    },
    {
      title: '合规与风控能力',
      description: '司机与交易风控、补贴效率监控与反作弊；满足企业级审计与税务要求。',
      icon: <Shield className="w-8 h-8" />
    },
    {
      title: '城市级运营方法论',
      description: '从选站/选店、活动日历到补能/维保周期化运营的可复制打法，支持快速规模化。',
      icon: <Globe className="w-8 h-8" />
    },
    {
      title: '联盟型权益体系',
      description: '跨品类（充电× 加油 × 维保 × 零售）组合权益，提升客单与复购，弱化价格战。',
      icon: <Network className="w-8 h-8" />
    }
  ];

  // 司机侧痛点与解法
  const driverSolutions = [
    {
      title: '场景化推荐',
      description: '基于实时位置，推送就近且优质服务的站点或门店',
      icon: <MapPin className="w-6 h-6" />
    },
    {
      title: '一键到站与即享权益',
      description: '导航直达＋到站券/会员价＋峰谷时段优惠',
      icon: <Target className="w-6 h-6" />
    },
    {
      title: '服务闭环',
      description: '预约维保、进度通知与质保查询',
      icon: <Repeat className="w-6 h-6" />
    }
  ];

  // 供给侧痛点与解法
  const supplySolutions = [
    {
      title: '精准获客与分发',
      description: '按城市/商圈/车型/能源偏好定向投放',
      icon: <Target className="w-6 h-6" />
    },
    {
      title: '经营中台',
      description: '活动统筹与宣发、A/B测试、拉新、复购/会员经营与LTV分析',
      icon: <BarChart3 className="w-6 h-6" />
    },
    {
      title: '统一结算与合规',
      description: '订单、核销、对账、税票与风控',
      icon: <FileText className="w-6 h-6" />
    },
    {
      title: '开放接口',
      description: '站点/门店API、SDK，快速对接存量系统',
      icon: <Settings className="w-6 h-6" />
    }
  ];

  // 价值主张
  const valuePropositions = [
    {
      title: '充电平台',
      description: '把"司机的高频补能需求"精准带到"更合适的时段与站点"，让桩更忙、成本更低、复购更高。',
      icon: <Zap className="w-8 h-8" />,
      color: 'blue'
    },
    {
      title: '加油平台',
      description: '抓住下沉与通勤刚需，用"场景化权益＋非油联动"让每一次到站都更值钱。',
      icon: <Fuel className="w-8 h-8" />,
      color: 'orange'
    },
    {
      title: '维修保养服务商',
      description: '基于里程与工况的"准时保养提醒＋透明套餐"，把规模、到店率与口碑同步做上去。',
      icon: <Wrench className="w-8 h-8" />,
      color: 'green'
    },
    {
      title: '具自有运力的网约车平台',
      description: '一体化的"补能供给+司机权益＋结算合规＋车辆健康"解决方案，轻松交付。',
      icon: <Car className="w-8 h-8" />,
      color: 'purple'
    }
  ];

  return (
    <div className="overflow-hidden">
      {/* 首屏 Hero - 优化视觉层次和布局 */}
      <section id="hero" ref={heroRef} className="relative pt-32 pb-32 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 overflow-hidden">
        {/* 背景装饰元素 */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-32 h-32 bg-blue-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-purple-400 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-300 rounded-full blur-3xl"></div>
        </div>
        
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center max-w-7xl mx-auto relative z-10"
          >
            {/* 标签徽章 */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={heroInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center px-6 py-3 bg-white/80 backdrop-blur-sm border border-blue-200 rounded-full text-blue-700 font-medium mb-8 shadow-lg"
            >
              <Car className="w-5 h-5 mr-2" />
              智能车后服务生态平台
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-3xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-6 leading-tight"
            >
              <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                车为家｜服务于网约车、出租车、物流司机的
              </span>
              <span className="block mt-4 text-4xl lg:text-6xl xl:text-7xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent font-extrabold">
                一站式车后服务平台
              </span>
            </motion.h1>
            
            {/* 装饰性分割线 */}
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={heroInView ? { opacity: 1, scaleX: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full mt-8"
            ></motion.div>
            
            {/* 车后服务介绍 - 融合到hero区 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/20 max-w-4xl mx-auto mt-12"
            >
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-gray-900">车后服务</h2>
              </div>
              
              <div className="text-xl text-gray-700 leading-relaxed space-y-4">
                <p>
                  我们连接司机侧的高频刚需（<span className="font-semibold text-green-600">充电、加油、维修保养、零售</span>）与供给侧的充电平台、加油平台、维修保养服务商，以及自有资产与运力但需配套车后服务的网约车平台。
                </p>
                <p>
                  通过<span className="font-semibold text-blue-600">"流量撮合＋交易结算＋经营中台＋开放API"</span>，实现稳定可转化的增长。
                </p>
              </div>
            </motion.div>
          </motion.div>
        </Container>
      </section>



      {/* 二、平台壁垒与差异化特点 - 环形布局设计 */}
      <section id="advantages" ref={advantagesRef} className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-64 h-64 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-64 h-64 bg-purple-500 rounded-full blur-3xl"></div>
        </div>
        
        <Container>
          {/* 优化标题设计 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={advantagesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-4">
              平台壁垒与差异化特点
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
          </motion.div>
          
          {/* 环形布局容器 */}
          <div className="max-w-7xl mx-auto">
            <div className="relative w-full h-[1000px] lg:h-[1100px] flex items-center justify-center">
              {/* 中心文字区域 */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={advantagesInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="absolute z-10 bg-gradient-to-br from-white to-gray-50 rounded-3xl p-8 lg:p-12 shadow-2xl border border-gray-200 max-w-md text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                  七大核心优势
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  构建车后服务生态的
                  <span className="block font-semibold text-blue-600">核心竞争壁垒</span>
                </p>
              </motion.div>
              
              {/* 7个组件环形排列 */}
              {platformAdvantages.map((advantage, index) => {
                // 计算每个组件的角度位置（360度分成7份）
                const angle = (index * 360) / 7 - 90; // -90度让第一个组件在顶部
                const radius = 380; // 环形半径 - 增加距离避免重叠
                const x = Math.cos((angle * Math.PI) / 180) * radius;
                const y = Math.sin((angle * Math.PI) / 180) * radius;
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.5, x: 0, y: 0 }}
                    animate={advantagesInView ? {
                      opacity: 1,
                      scale: 1,
                      x: x,
                      y: y
                    } : {}}
                    transition={{
                      duration: 0.8,
                      delay: 0.5 + index * 0.1,
                      type: "spring",
                      stiffness: 100
                    }}
                    className="absolute group cursor-pointer"
                    style={{
                      transform: `translate(-50%, -50%)`,
                    }}
                  >
                    {/* 连接线 */}
                    <div 
                      className="absolute w-0.5 bg-gradient-to-r from-blue-300 to-purple-300 opacity-30 group-hover:opacity-60 transition-opacity duration-300"
                      style={{
                        height: `${radius - 140}px`,
                        left: '50%',
                        top: '50%',
                        transformOrigin: 'top center',
                        transform: `translateX(-50%) rotate(${angle + 90}deg)`,
                      }}
                    />
                    
                    {/* 组件卡片 */}
                    <div className="relative w-48 lg:w-56">
                      {/* 卡片阴影装饰 */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl transform rotate-1 group-hover:rotate-2 transition-transform duration-300 opacity-50"></div>
                      <div className="relative bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 group-hover:scale-105">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 text-white shadow-lg group-hover:scale-110 transition-transform duration-300 mx-auto">
                          {advantage.icon}
                        </div>
                        <h4 className="text-lg font-bold text-gray-900 mb-3 text-center">{advantage.title}</h4>
                        <p className="text-sm text-gray-700 leading-relaxed text-center">{advantage.description}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </Container>
      </section>

      {/* 流量入口模块 - 优化布局和视觉效果 */}
      <section className="py-24 bg-gradient-to-br from-indigo-50 to-blue-50 relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 right-20 w-48 h-48 bg-blue-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-48 h-48 bg-indigo-400 rounded-full blur-3xl"></div>
        </div>
        
        <Container>
          {/* 优化标题设计 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-4">
              流量入口
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 mx-auto rounded-full"></div>
          </motion.div>
          
          {/* 流量入口布局：横向排列所有图片 */}
          <div className="max-w-7xl mx-auto">
            {/* 横向排列所有图片和箭头 */}
            <motion.div 
              className="flex items-center justify-center gap-6 lg:gap-8 flex-wrap lg:flex-nowrap"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* 司机接单软件 */}
              <div className="flex flex-col items-center group">
                <div className="relative">
                  <img 
                    src="/driver-app.png" 
                    alt="司机接单软件"
                    className="w-auto h-64 lg:h-72 object-contain rounded-lg shadow-lg group-hover:shadow-2xl transition-all duration-300"
                    loading="lazy"
                  />
                </div>
                {/* 文字标签 - 与其他模块高度对齐 */}
                <div className="mt-4 h-16 flex items-center">
                  <div className="bg-gradient-to-br from-blue-100 to-blue-200 bg-opacity-90 p-4 rounded-lg shadow-md group-hover:shadow-lg transition-all duration-300">
                    <h3 className="text-lg lg:text-xl font-semibold text-gray-900 text-center">司机接单软件</h3>
                  </div>
                </div>
              </div>

              {/* 第一个箭头 */}
              <div className="flex items-center justify-center flex-shrink-0">
                <svg className="w-8 h-8 lg:w-10 lg:h-10 text-blue-500 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M13.025 1l-2.847 2.828 6.176 6.176h-16.354v3.992h16.354l-6.176 6.176 2.847 2.828 10.975-11z"/>
                </svg>
              </div>

              {/* 优惠充电/优惠加油 */}
              <div className="flex flex-col items-center group">
                <div className="relative">
                  <img 
                    src="/discount-services.png" 
                    alt="优惠充电优惠加油"
                    className="w-auto h-64 lg:h-72 object-contain rounded-lg shadow-lg group-hover:shadow-2xl transition-all duration-300"
                    loading="lazy"
                  />
                </div>
                {/* 文字标签 - 与其他模块高度对齐 */}
                <div className="mt-4 h-16 flex items-center">
                  <div className="bg-gradient-to-br from-blue-100 to-blue-200 bg-opacity-90 p-4 rounded-lg shadow-md group-hover:shadow-lg transition-all duration-300">
                    <h3 className="text-lg lg:text-xl font-semibold text-gray-900 text-center">优惠充电/优惠加油</h3>
                  </div>
                </div>
              </div>

              {/* 第二个箭头 */}
              <div className="flex items-center justify-center flex-shrink-0">
                <svg className="w-8 h-8 lg:w-10 lg:h-10 text-blue-500 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M13.025 1l-2.847 2.828 6.176 6.176h-16.354v3.992h16.354l-6.176 6.176 2.847 2.828 10.975-11z"/>
                </svg>
              </div>

              {/* 车为家-充电和车为家-加油（并排显示，调换位置，去掉中间箭头） */}
              <div className="flex flex-col items-center group">
                {/* 图片3和4并列容器 */}
                <div className="flex items-center">
                  {/* 车为家-充电（原来的第4张图片，现在放在第3位） */}
                  <div className="relative">
                    <img 
                      src="/cheweijia-charging.png" 
                      alt="车为家-充电"
                      className="w-auto h-64 lg:h-72 object-contain rounded-lg shadow-lg group-hover:shadow-2xl transition-all duration-300"
                      loading="lazy"
                    />
                  </div>

                  {/* 车为家-加油（原来的第3张图片，现在放在第4位，紧密贴合） */}
                  <div className="relative">
                    <img 
                      src="/cheweijia-refuel.png" 
                      alt="车为家-加油"
                      className="w-auto h-64 lg:h-72 object-contain rounded-lg shadow-lg group-hover:shadow-2xl transition-all duration-300"
                      loading="lazy"
                    />
                  </div>
                </div>
                
                {/* 车为家-加油/充电频道标题 - 与其他模块高度对齐 */}
                <div className="mt-4 h-16 flex items-center">
                  <div className="bg-gradient-to-br from-blue-100 to-blue-200 bg-opacity-90 p-4 rounded-lg shadow-md group-hover:shadow-lg transition-all duration-300">
                    <h3 className="text-lg lg:text-xl font-semibold text-gray-900 text-center">车为家-充电/加油频道</h3>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* 三、我们是如何解决两端问题的 */}
      <section id="solutions" ref={solutionsRef} className="py-20 bg-white">
        <Container>
          <SectionTitle
            title="我们是如何解决两端问题的"
            subtitle=""
            centered
          />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* 司机侧（需求端） */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={solutionsInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mr-4">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">司机侧（需求端）</h3>
              </div>
              
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">痛点：</h4>
                <p className="text-gray-700 mb-4">高频刚需但时间敏感、价格敏感；信息不对称（距离/价格/活动等）；</p>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">我们的解法：</h4>
                <div className="space-y-4">
                  {driverSolutions.map((solution, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                        {solution.icon}
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900">{solution.title}：</h5>
                        <p className="text-gray-700 text-sm">{solution.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
            
            {/* 供给侧 */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={solutionsInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mr-4">
                  <Building className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">供给侧（充电/加油/维保等）</h3>
              </div>
              
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">痛点：</h4>
                <p className="text-gray-700 mb-4">获客成本高、流量分散；站效/人工利用不足；价格战导致利润稀释；缺乏数字化能力；难以量化营销效果与复购。</p>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">我们的解法：</h4>
                <div className="space-y-4">
                  {supplySolutions.map((solution, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                        {solution.icon}
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900">{solution.title}：</h5>
                        <p className="text-gray-700 text-sm">{solution.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* 价值主张 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={solutionsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">价值主张</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {valuePropositions.map((proposition, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className={`w-16 h-16 rounded-xl flex items-center justify-center mb-4 ${
                    proposition.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                    proposition.color === 'orange' ? 'bg-orange-100 text-orange-600' :
                    proposition.color === 'green' ? 'bg-green-100 text-green-600' :
                    'bg-purple-100 text-purple-600'
                  }`}>
                    {proposition.icon}
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">{proposition.title}：</h4>
                  <p className="text-gray-700 leading-relaxed">{proposition.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </Container>
      </section>

      {/* 四、对您的收益 - 优化布局和视觉效果 */}
      <section id="benefits" ref={benefitsRef} className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/4 left-10 w-64 h-64 bg-blue-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-10 w-64 h-64 bg-green-400 rounded-full blur-3xl"></div>
        </div>
        
        <Container>
          {/* 优化标题设计 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-4">
              对您的收益
            </h2>
            <p className="text-xl text-gray-600 mb-6">双方共赢</p>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-green-500 mx-auto rounded-full"></div>
          </motion.div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-7xl mx-auto">
            {/* 网约车平台 - 增强视觉效果 */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={benefitsInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="relative group"
            >
              {/* 背景装饰 */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-100 to-purple-200 rounded-3xl transform rotate-1 group-hover:rotate-2 transition-transform duration-300"></div>
              <div className="relative bg-white rounded-3xl p-10 shadow-xl hover:shadow-2xl transition-all duration-300 border border-purple-100">
                <div className="flex items-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
                    <Car className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">网约车平台</h3>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-purple-600 mb-2">核心收益：</h4>
                    <p className="text-gray-700">更高司机留存与接单时长、可控的营销成本、统一结算与合规风控。</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-purple-600 mb-2">关键词：</h4>
                    <div className="flex flex-wrap gap-2">
                      {['司机权益中心', '能量币', '月结与税票', '城市级运营策略'].map((keyword, index) => (
                        <span key={index} className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium">
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-purple-600 mb-3">常用KPI：</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {['司机月活↑', '权益使用率↑', '营销成本↓', '流失率↓'].map((kpi, index) => (
                        <div key={index} className="bg-purple-50 rounded-lg p-3 text-center">
                          <span className="text-purple-700 font-medium">{kpi}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* 充电/加油/维保等服务商 - 增强视觉效果 */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={benefitsInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative group"
            >
              {/* 背景装饰 */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-100 to-orange-200 rounded-3xl transform -rotate-1 group-hover:-rotate-2 transition-transform duration-300"></div>
              <div className="relative bg-white rounded-3xl p-10 shadow-xl hover:shadow-2xl transition-all duration-300 border border-orange-100">
                <div className="flex items-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-600 to-orange-700 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
                    <Building className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">充电/加油/维保等服务商</h3>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-orange-600 mb-2">核心收益：</h4>
                    <p className="text-gray-700">稳定到站客流与更高站效、整体规模与收入提升。</p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-orange-600 mb-2">关键词：</h4>
                    <div className="flex flex-wrap gap-2">
                      {['LBS 场景化推荐', '流量撮合', '活动统一管理', '券核销与对账', '会员体系'].map((keyword, index) => (
                        <span key={index} className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-orange-600 mb-3">常用KPI：</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {['到站转化↑', '规模与收入↑', '渗透率↑', '复购率↑'].map((kpi, index) => (
                        <div key={index} className="bg-orange-50 rounded-lg p-3 text-center">
                          <span className="text-orange-700 font-medium">{kpi}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>


    </div>
  );
};

export default CarServicePage;